// Thin adapter for the chat widget. It does NOT introduce any new backend
// endpoints — it simply re-exports existing app APIs (with the app's auth /
// token-refresh interceptors) under names the widget understands.
//
// Spec name (FEATURES.md)  ->  existing API
//   getFarms()             ->  getListofFarms_active(userName)   (farms with a running batch only)
//   getActiveBatches()     ->  getListofBatches(farmID, 'active')   (v1: active only)
//   getDays()              ->  getBatchOperationsData(batchID)
//   getExistingValue()     ->  getDailyOperationsStatus(batchID, date)
//   saveEntry()            ->  addDailyParams([...])
//   getGrowthCurve()       ->  getGrowthCurve(batchID)  (all-days values + batch metadata)

import * as api from "../utils/axiosinstace";
import { getUserName } from "../utils/common";
import aiInstance from "./aiChatApi";
import i18n from "../../i18n";
import { normalizeLng } from "../../i18n/locale";

// Only farms that currently have a running (active) batch — same source the
// Batch Operations screen uses, so there's nothing to select that has no batch.
export const getFarms = () => api.getListofFarms_active(getUserName());

// Admin-only: pick a farmer first, then list THAT farmer's active-batch farms.
// (Admins have no farms of their own, so getFarms() above is empty for them.)
export const getFarmers = () => api.getListofFarmers_admin();
export const getFarmsForFarmer = (farmerID) => api.getListofFarms_active_admin(farmerID);

// v1 scope: active batches only.
export const getActiveBatches = (farmID) => api.getListofBatches(farmID, "active");

export const getDays = (batchID) => api.getBatchOperationsData(batchID);

export const getExistingValue = (batchID, date) =>
  api.getDailyOperationsStatus(batchID, date);

export const saveEntry = (payloadArray) => api.addDailyParams(payloadArray);

// Batch metadata for the summary card (breed, birds, start date, duration…).
export const getGrowthCurve = (batchID) => api.getGrowthCurve(batchID);

// Bulk per-day entry status (one object per day with _status flags) — drives
// the day-grid completeness rings. Falls back gracefully if unavailable.
export const getDayStatuses = (batchID) => api.getBatchDayStatuses(batchID);

// ---- Django report service (the same one the mobile app uses) -----------
// Reached through the AI-service axios instance: same origin (/chatbot/api),
// same bearer token and single-flight refresh. Paths verified against the
// deployed host (…/chatbot/api/reports/* answers 401 without a token, i.e.
// the routes exist).

// The language the service should WRITE ITS PROSE IN. Resolved here, at the
// wire boundary, rather than at the call sites: the widget builds the report
// payload in one place and the chat FormData in another (two different shapes),
// and a future caller of this module would otherwise have to remember it.
//
// Read off i18next because that is where all three sources of a language have
// already converged — localStorage 'lang', the stored user object, and the
// server-side preference that applyServerLanguage() folds in at login
// (i18n/index.js, called from Login.js). So this follows the user across
// devices without reading the server field again.
//
// resolvedLanguage, not language: `language` is the REQUESTED tag and can carry
// a region if changeLanguage() is ever called with one — i18n/i18n.test.js
// asserts exactly that distinction. normalizeLng is total on top of that, so
// anything at all (null, 'en-US', 'fr') leaves here as exactly 'en' or 'ms' and
// the service can never see a code it does not know.
const currentLanguage = () => normalizeLng(i18n.resolvedLanguage || i18n.language);

// { report_type: today|yesterday|weekly|custom_range, farm_id, batch_id,
//   start_date?, end_date?, language }  ->  LLM-scored analysis of the entered
// data. Every report type funnels through here, so all of them get `language`.
export const generateReport = (payload) =>
  aiInstance
    .post("/reports/generate-report/", { ...payload, language: currentLanguage() })
    .then((r) => r.data);

// { report_type: "phase_wise", farm_id, batch_id, language } -> per-phase rollup.
export const generatePhaseWiseReport = (payload) =>
  aiInstance
    .post("/reports/generate-phase-wise-report/", { ...payload, language: currentLanguage() })
    .then((r) => r.data);

// Free-text "Ask Roo anything" grounded in the current report context.
// FormData fields: message, report_analysis (JSON string), farm_id, batch_id,
// report_type, image, language — all optional except (message or image).
export const sendReportChat = (formData) => {
  // Multipart, so this is a field on the form rather than an object key. set()
  // rather than append() so the part cannot be duplicated if a caller ever
  // reuses a FormData across retries.
  formData.set("language", currentLanguage());
  return aiInstance.post("/reports/chat/", formData).then((r) => r.data);
};
